import { useNoshStore } from '../lib/store';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { motion } from 'framer-motion';
import { daysUntilExpiry, getExpiryColor, formatCalories, formatMacronutrient, formatRemainingCalories } from '../lib/utils';
import { Search, Camera, PlusCircle, Receipt, Sparkles, ChevronRight, Heart, Book, CalendarDays, Activity, ShoppingCart, Settings, ChefHat, ClipboardList, Calendar, Bell } from 'lucide-react';
import { announcementUtils } from '../config/announcements';
import { announcementConfigManager } from '../lib/announcement-config-manager';
import { AnnouncementDropdown } from '../components/ui/announcement-dropdown';
import { useState, useEffect, useRef } from 'react';
// import { ThemeToggle } from '../components/ui/theme-toggle';
import { useGlobalImageScanner } from '../lib/global-image-scan';
import { DailyNutritionProgress } from '../components/ui/daily-nutrition-progress';
import { db, DailyNutritionSummary } from '../lib/database';
import { calculateNutritionTargets, type DietaryPreference } from '../lib/nutrition-calculator';

import { useNavigate, useSearchParams } from 'react-router-dom';
import NutritionAnalysisModal from '../components/NutritionAnalysisModal';
import { MealGenerationModal, MealGenerationOptions } from '../components/ui/meal-generation-modal';
// UsageLimitModal now handled globally in App.tsx
import { IngredientSourceModal } from '../components/ui/ingredient-source-modal';

import { getHeroIngredients, calculateNutritionPercentages, getNutritionStatusColor } from '../components/ui/recipe-card-utils';
import { getImageDisplayUrl } from '../lib/api';
import { useMobileTouchHandler } from '../hooks/useMobileTouchHandler';
import { CachedImage } from '../components/ui/CachedImage';
import { useLeftEdgeSwipeDisable } from '../hooks/useLeftEdgeSwipeDisable';
import { getOptimizedImageUrl, migrateToOptimizedUrl, getUniversalOptimizedImageUrl } from '../lib/image-optimization';
import { supabase } from '../lib/supabase';
import { useToast } from '../components/ui/use-toast';

// Animated AI Sparkles Component
const AnimatedSparkles = ({ size }: { size: number }) => (
  <Sparkles 
    size={size} 
    className="animate-pulse"
    style={{
      animation: 'colorCycle 3s ease-in-out infinite',
      animationTimingFunction: 'ease-in-out'
    }}
  />
);

// Smart Cached Meal Image Component - user uploads use signed URLs, AI images use optimization
const CachedMealImage = ({ imagePathOrUrl, alt, className, size = 'meal-tracker' }: { 
  imagePathOrUrl: string; 
  alt: string; 
  className: string; 
  size?: 'meal-tracker' | 'recipe-card-small' | 'recipe-card';
}) => {
  const [finalUrl, setFinalUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadImageUrl = async () => {
      try {
        setIsLoading(true);
        
        // Check if imagePathOrUrl is empty or null
        if (!imagePathOrUrl || imagePathOrUrl.trim() === '') {
          setFinalUrl('/food.jpg'); // Use fallback for empty paths
          return;
        }
        
        // Check if this is a user-uploaded image (images bucket) or AI-generated (recipe-images bucket)
        const isUserUpload = imagePathOrUrl.includes('/images/') || 
                           (!imagePathOrUrl.includes('http') && !imagePathOrUrl.includes('recipe-images'));
        
        if (isUserUpload) {
          // User-uploaded images: Use signed URLs (they work reliably)
          const signedUrl = await getImageDisplayUrl(imagePathOrUrl, 86400); // 24 hour expiry
          setFinalUrl(signedUrl);
        } else {
          // AI-generated images: Use optimization
          const optimized = getOptimizedImageUrl(imagePathOrUrl, size, {
            fallbackUrl: '/food.jpg',
            quality: 90
          });
          setFinalUrl(optimized);
        }
      } catch (error) {
        console.warn('⚠️ [Dashboard] Failed to load image:', error);
        setFinalUrl('/food.jpg'); // Fallback
      } finally {
        setIsLoading(false);
      }
    };

    loadImageUrl();
  }, [imagePathOrUrl, size]);

  if (isLoading) {
    return (
      <div className={`${className} bg-gray-200 animate-pulse flex items-center justify-center`}>
        <div className="text-gray-400 text-sm">Loading...</div>
      </div>
    );
  }

  // Use CachedImage for the actual image display with caching
  return (
    <CachedImage
      src={finalUrl}
      alt={alt}
      className={`${className} transition-opacity duration-300`}
      loading="lazy"
      fallbackSrc="/food.jpg"
    />
  );
};

const Dashboard = () => {
  const { 
    inventory, 
    recipes, 
    meals,
    expiringItems, 
    user, 
    loadAllData, 
    isOnline, 
    showNutritionAnalysis, 
    showScanResults,
    ingredientSource,
    showIngredientSourceModal,
    hideIngredientSourceModal,
    setSelectedIngredientSource,
    getMealById,

  } = useNoshStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mealScanInputRef = useRef<HTMLInputElement>(null);
  const mobileInventoryCameraRef = useRef<HTMLInputElement>(null);
  const mobileMealCameraRef = useRef<HTMLInputElement>(null);
  const [currentCard, setCurrentCard] = useState(0);
  const [isAnnouncementDropdownOpen, setIsAnnouncementDropdownOpen] = useState(false);
  const [isPwaAnnouncementDropdownOpen, setIsPwaAnnouncementDropdownOpen] = useState(false);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const pwaBellButtonRef = useRef<HTMLButtonElement>(null);
  const swipeContainerRef = useRef<HTMLDivElement>(null);
  
  // Lazy loading state for Recent Recipes
  const [visibleRecipeCount, setVisibleRecipeCount] = useState(5);
  const [desktopVisibleCount, setDesktopVisibleCount] = useState(3);
  const recipeScrollRef = useRef<HTMLDivElement>(null);

  // Lazy loading state for Recent Meal Logs
  const [visibleMealCount, setVisibleMealCount] = useState(5); // Mobile lazy loading
  const [desktopVisibleMealCount, setDesktopVisibleMealCount] = useState(3);
  const mealScrollRef = useRef<HTMLDivElement>(null);

  // Use global image scanner
  const { isScanning } = useGlobalImageScanner();

  // Nutrition Analysis Modal state (same as MealTracker)
  const [showNutritionModal, setShowNutritionModal] = useState(false);
  const [selectedMealId, setSelectedMealId] = useState<string | null>(null);

  // Recipe generation state
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  
  // Usage limit modal now handled globally - removed individual state

  // Handle OAuth redirect from Google sign-in
  useEffect(() => {
    const handleOAuthRedirect = async () => {
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Check for OAuth cancellation
      if (error === 'access_denied' || 
          (error && errorDescription && errorDescription.toLowerCase().includes('cancel'))) {
        
        console.log('🚫 OAuth cancellation detected on dashboard, redirecting to login');
        
        toast({
          title: "Google sign-in cancelled",
          description: "You cancelled the Google sign-in process. Redirecting to login page...",
          variant: "default",
        });

        // Clean up URL and redirect to login
        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState({}, document.title, url.pathname);
        
        setTimeout(() => {
          navigate('/auth/login');
        }, 1500);
        return;
      }

      // Handle successful OAuth
      if (accessToken && refreshToken) {
        console.log('✅ OAuth success detected on dashboard, setting session');
        
        try {
          // Set the session with the OAuth tokens
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) throw sessionError;

          // Show success message
          toast({
            title: "Welcome to Nosh! 🎉",
            description: "Successfully signed in with Google. Loading your dashboard...",
          });

          // Clean up URL parameters
          const url = new URL(window.location.href);
          url.search = '';
          window.history.replaceState({}, document.title, url.pathname);

          // Reload user data after successful authentication
          setTimeout(() => {
            loadAllData();
          }, 1000);

        } catch (error: any) {
          console.error('❌ Error setting OAuth session on dashboard:', error);
          toast({
            title: "Sign-in error",
            description: "There was an issue completing your Google sign-in. Please try again.",
            variant: "destructive",
          });
          
          // Clean up URL and redirect to login on error
          const url = new URL(window.location.href);
          url.search = '';
          window.history.replaceState({}, document.title, url.pathname);
          
          setTimeout(() => {
            navigate('/auth/login');
          }, 2000);
        }
      }
    };

    handleOAuthRedirect();
  }, [searchParams, navigate, toast, loadAllData]);

  // Prevent browser native left edge swipe gesture on mobile
  useLeftEdgeSwipeDisable({
    pageId: 'Dashboard',
    enabled: true,
    edgeWidth: 50
  });

  // Swipe functionality for header cards
  const handleSwipe = (direction: 'left' | 'right') => {
    if (direction === 'left' && currentCard < 1) {
      setCurrentCard(1);
    } else if (direction === 'right' && currentCard > 0) {
      setCurrentCard(0);
    }
  };

  // Touch event handlers for swipe
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleSwipe('left');
    } else if (isRightSwipe) {
      handleSwipe('right');
    }
  };

  // Dashboard mount logging - App.tsx handles all data loading
  useEffect(() => {
    // Dashboard component mounted
  }, []); // Empty dependency array - only run on mount

  // Real nutrition data from same sources as Profile page
  const [nutritionSummary, setNutritionSummary] = useState<DailyNutritionSummary | null>(null);
  const [nutritionTargets, setNutritionTargets] = useState<any>(null);
  // Use singleton database service instead of creating new instance

  // Load nutrition data (same logic as DailyNutritionProgress component)
  useEffect(() => {
    const loadNutritionData = async () => {
      try {
        // Load today's nutrition summary
        const summary = await db.getDailyNutritionSummary();
        setNutritionSummary(summary);

        // Calculate nutrition targets if user has body stats
        if (user?.age && user?.gender && user?.height && user?.weight && user?.activityLevel) {
          const targets = calculateNutritionTargets({
            age: user.age,
            gender: user.gender,
            height: user.height,
            weight: user.weight,
            activityLevel: user.activityLevel,
            goal: user.goal || 'maintain',
            dietaryPreferences: (user.preferences || []) as DietaryPreference[]
          });
          setNutritionTargets(targets);
        }
      } catch (error) {
        console.error('❌ Error loading nutrition data:', error);
      }
    };

    loadNutritionData();
  }, [user, meals]); // Reload when user data or meals change

  // Calculate nutrition data for display (matching DailyNutritionProgress logic)
  const nutritionData = nutritionSummary && nutritionTargets ? {
    calories: Math.round(nutritionSummary.totalCalories || 0),
    calorieGoal: Math.round(nutritionTargets.dailyCalories || 2000),
    protein: Math.round(nutritionSummary.totalProtein || 0),
    proteinGoal: Math.round(nutritionTargets.macroTargets?.protein?.target || 150),
    carbs: Math.round(nutritionSummary.totalCarbs || 0),
    carbGoal: Math.round(nutritionTargets.macroTargets?.carbs?.target || 250),
    fat: Math.round(nutritionSummary.totalFat || 0),
    fatGoal: Math.round(nutritionTargets.macroTargets?.fat?.target || 100),
    fiber: Math.round(nutritionSummary.totalFiber || 0),
    fiberGoal: Math.round(nutritionTargets.macroTargets?.fiber?.target || 25),
    mealsToday: (nutritionSummary.mealsCount || 0) + (nutritionSummary.recipesCount || 0)
  } : {
    // Fallback values when data is loading
    calories: 0,
    calorieGoal: 2000,
    protein: 0,
    proteinGoal: 150,
    carbs: 0,
    carbGoal: 250,
    fat: 0,
    fatGoal: 100,
    fiber: 0,
    fiberGoal: 25,
    mealsToday: 0
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);



  // Note: Mobile scan events are now handled globally in Layout.tsx
  // This ensures scan functionality works on all pages, not just Dashboard

  // Handler for scan image using global scanner

  // Trigger file input (desktop uses file picker, mobile uses camera)
  const triggerFileInput = () => {
    if (!isMobile && fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    } else if (isMobile) {
      // For mobile standalone, trigger camera directly
      if (mobileInventoryCameraRef.current) {
        mobileInventoryCameraRef.current.value = '';
        mobileInventoryCameraRef.current.click();
      }
    }
  };

  // Trigger meal scan input (desktop uses file picker, mobile uses camera)
  const triggerMealScanInput = () => {
    if (!isMobile && mealScanInputRef.current) {
      mealScanInputRef.current.value = '';
      mealScanInputRef.current.click();
    } else if (isMobile) {
      // For mobile standalone, trigger meal camera directly
      if (mobileMealCameraRef.current) {
        mobileMealCameraRef.current.value = '';
        mobileMealCameraRef.current.click();
      }
    }
  };

  // Handle nutrition modal close - cleanup if meal not saved (same as MealTracker)
  const handleNutritionModalClose = () => {
    setShowNutritionModal(false);
    setSelectedMealId(null);
  };

  // Handle ingredient source selection
  const handleIngredientSourceSelected = (sourceType: 'inventory' | 'mixed' | 'photo', photoIngredients?: string[]) => {
    console.log('🎯 [DASHBOARD] Ingredient source selected:', sourceType, photoIngredients);
    
    // Store the selected source and detected ingredients
    setSelectedIngredientSource(sourceType, photoIngredients);
    
    // Show the meal generation modal
    setShowRecipeModal(true);
  };

  // Handle back from meal generation to ingredient source
  const handleMealGenerationBack = () => {
    console.log('🔙 [DASHBOARD] User wants to go back to ingredient source');
    
    // Close meal generation modal
    setShowRecipeModal(false);
    
    // Reopen ingredient source modal
    showIngredientSourceModal();
  };

  const handleRecipeGenerate = async (options: MealGenerationOptions) => {
    console.log('🎯 [DASHBOARD] handleRecipeGenerate called!');
    console.log('🎯 [DASHBOARD] Options received:', JSON.stringify(options, null, 2));
    console.log('🎯 [DASHBOARD] Detected ingredients:', ingredientSource.detectedIngredients);
    
    const { 
      generateRecipeWithAI, 
      generateRecipeFromPhoto,
      startRecipeGeneration,
      updateRecipeGenerationProgress,
      completeRecipeGeneration,
      setRecipeGenerationError,
      cancelRecipeGeneration,
      showRecipeResult
    } = useNoshStore.getState();
    
    console.log('🎯 [DASHBOARD] Store functions loaded, starting generation...');
    
    try {
      console.log('🔄 [DASHBOARD] Starting loading modal...');
      // Start the loading modal
      startRecipeGeneration();
      
      // Check if we have detected ingredients from photo
      const hasDetectedIngredients = ingredientSource.detectedIngredients && ingredientSource.detectedIngredients.length > 0;
      
      let result;
      if (hasDetectedIngredients) {
        console.log('📸 [DASHBOARD] Using photo-detected ingredients, calling generateRecipeFromPhoto...');
        console.log('📸 [DASHBOARD] Detected ingredients:', ingredientSource.detectedIngredients);
        
        // Call the photo-specific AI generation function
        result = await generateRecipeFromPhoto(ingredientSource.detectedIngredients, options);
        console.log('📥 [DASHBOARD] generateRecipeFromPhoto result:', result);
      } else {
        console.log('🤖 [DASHBOARD] Using regular inventory, calling generateRecipeWithAI...');
        
        // Call the regular AI generation function
        result = await generateRecipeWithAI(options);
        console.log('📥 [DASHBOARD] generateRecipeWithAI result:', result);
      }
      
      if (result.success && result.recipe) {
        console.log('✅ [DASHBOARD] Recipe generated successfully!');
        console.log('✅ [DASHBOARD] Recipe name:', result.recipe.name);
        
        // Show the generated recipe with proper photo context
        console.log('🔄 [DASHBOARD] Calling showRecipeResult...');
        // Include detected ingredients in generation options for photo recipes
        const enhancedOptions = hasDetectedIngredients 
          ? { ...options, detectedIngredients: ingredientSource.detectedIngredients, source: 'photo' }
          : options;
        
        showRecipeResult(result.recipe, enhancedOptions);
        console.log('✅ [DASHBOARD] showRecipeResult call completed with enhanced options:', enhancedOptions);
      } else {
        console.error('❌ [DASHBOARD] Recipe generation failed:', result.error);
        setRecipeGenerationError(result.error || 'Recipe generation failed');
      }
      
    } catch (error) {
      console.error('❌ [DASHBOARD] Failed to generate recipe:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate recipe';
      setRecipeGenerationError(errorMessage);
    }
  };

  // Lazy loading handler for recipes (mobile)
  const handleRecipeScroll = () => {
    if (!recipeScrollRef.current || !isMobile) return;
    
    const container = recipeScrollRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    
    // Check if user has scrolled near the end (within 200px)
    const nearEnd = scrollLeft + clientWidth >= scrollWidth - 200;
    
    if (nearEnd && visibleRecipeCount < recipes.length) {
      const newCount = Math.min(visibleRecipeCount + 5, recipes.length);
      console.log(`🔄 [Dashboard] Loading more recipes: ${visibleRecipeCount} → ${newCount}`);
      setVisibleRecipeCount(newCount);
    }
  };

  // Lazy loading handler for meals (mobile)
  const handleMealScroll = () => {
    if (!mealScrollRef.current || !isMobile) return;
    
    const container = mealScrollRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    
    // Check if user has scrolled near the end (within 200px)
    const nearEnd = scrollLeft + clientWidth >= scrollWidth - 200;
    
    if (nearEnd && visibleMealCount < meals.length) {
      const newCount = Math.min(visibleMealCount + 5, meals.length);
      console.log(`🔄 [Dashboard] Loading more meals: ${visibleMealCount} → ${newCount}`);
      setVisibleMealCount(newCount);
    }
  };

  // Load more handler for desktop
  const handleLoadMoreRecipes = () => {
    const newCount = Math.min(desktopVisibleCount + 3, recipes.length);
    console.log(`🔄 [Dashboard Desktop] Loading more recipes: ${desktopVisibleCount} → ${newCount}`);
    setDesktopVisibleCount(newCount);
  };

  // Load more handler for meals
  const handleLoadMoreMeals = () => {
    const newCount = Math.min(desktopVisibleMealCount + 3, meals.length);
    console.log(`🔄 [Dashboard Desktop] Loading more meals: ${desktopVisibleMealCount} → ${newCount}`);
    setDesktopVisibleMealCount(newCount);
  };

  // Handle meal click for nutrition analysis (same as MealTracker)
  const handleMealClick = async (mealId: string) => {
    const meal = getMealById(mealId);
    if (meal) {
      // Use the same pattern as MealTracker for consistency
      console.log('🍽️ [Dashboard] Meal clicked, preparing modal data without duplicate URL generation');
      
      // Store the meal data in sessionStorage so the modal can access it
      const mealAnalysisData = {
        mealData: {
          meal_name: meal.mealName,
          serving_size: meal.servingSize,
          estimated_calories: meal.calories,
          macros: {
            protein: meal.protein,
            carbs: meal.carbs,
            fat: meal.fat
          },
          nutrition: {
            fiber: meal.fiber,
            sugar: meal.sugar,
            sodium: meal.sodium
          },
          ingredients: meal.ingredients,
          confidence: meal.confidence,
          ai_suggestions: meal.aiSuggestions
        },
        imageUrl: meal.imageUrl, // Use the permanent storage path - modal will handle URL generation
        timestamp: new Date().toISOString()
      };
      
      console.log('💾 [Dashboard] Storing meal data for modal (no duplicate URL generation):', { 
        mealId, 
        hasImageUrl: !!meal.imageUrl 
      });
      
      sessionStorage.setItem('mealAnalysisData', JSON.stringify(mealAnalysisData));
      setSelectedMealId(mealId);
      setShowNutritionModal(true);
    }
  };

  // Format date helper (same as MealTracker)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Format logging date for the top banner
  const formatLoggingDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Reset time to compare dates only
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const mealDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffTime = today.getTime() - mealDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'logged today';
    } else if (diffDays === 1) {
      return 'logged yesterday';
    } else {
      return `logged ${date.toLocaleDateString()}`;
    }
  };

  return (
    <div 
      className={`max-w-6xl mx-auto space-y-6 ${isMobile ? 'pb-20' : ''}`}
      style={{
        // Only prevent navigation overscroll - preserve all other gestures
        overscrollBehaviorX: 'none',
        touchAction: 'auto', // Allow all touch gestures including horizontal scroll
        WebkitOverflowScrolling: 'touch',
        // Additional mobile-specific prevention
        ...(isMobile && {
          position: 'relative',
          isolation: 'isolate'
        })
      }}
    >
      {/* Desktop Header - only show on desktop */}
      {!isMobile && (
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">Welcome to Nosh 😋</h1>
            <p className="text-muted-foreground mt-1">Scan, track, and transform your ingredients into amazing meals!</p>
          </div>
          <div className="flex items-center">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            >
              <div className="relative">
                <Button 
                  ref={bellButtonRef}
                  variant="ghost" 
                  size="icon" 
                  className="relative h-10 w-10 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 border-0 rounded-full transition-colors duration-200"
                  onClick={() => {
                    setIsAnnouncementDropdownOpen(!isAnnouncementDropdownOpen);
                  }}
                >
                  <Bell size={18} />
                  {announcementConfigManager.shouldShowRedDot() && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                  )}
                </Button>
                
                <AnnouncementDropdown
                  isOpen={isAnnouncementDropdownOpen}
                  onClose={() => setIsAnnouncementDropdownOpen(false)}
                  anchorRef={bellButtonRef}
                />
              </div>
            </motion.div>
          </div>
        </div>
      )}



      {/* Desktop Daily Nutrition Progress - Compact White Design */}
      {!isMobile && (
        <div className="mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-4"
          >
            <DailyNutritionProgress 
              className="bg-transparent border-0 p-0"
              isMobile={false}
            />
          </motion.div>
        </div>
      )}

      {/* Mobile Swipeable Header Cards - only show on mobile */}
      {isMobile && (
        <div className="relative w-full overflow-hidden pt-6">
          <div 
            ref={swipeContainerRef}
            className="flex transition-transform duration-300 ease-out"
            style={{
              transform: `translateX(-${currentCard * 50}%)`,
              width: '200%'
            }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Card 1: Nutrition Progress Card */}
            <div className="w-1/2 flex-shrink-0">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="relative overflow-hidden bg-white rounded-3xl shadow-lg"
                style={{
                  minHeight: '200px'
                }}
              >
                <div className="relative z-10 p-6">
                  {/* Top Section */}
                  <div className="flex items-start justify-between mb-4">
                    {/* Left Side - Welcome Text */}
                    <div className="flex-1 space-y-1">
                      <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                        className="text-black text-2xl font-heading font-bold leading-tight"
                      >
                        What's cooking, {user.name?.split(' ')[0] || 'Chef'}? 😋
                      </motion.h1>
                    </div>
                    
                    {/* Right Side - Avatar */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      className="ml-2"
                    >
                      <div className="h-16 w-16 flex items-center justify-center">
                        <img 
                          src={user.avatar || '/img/avatars/avatar-1.png'} 
                          alt={user.name || 'User'}
                          className="h-16 w-16 object-contain"
                        />
                      </div>
                    </motion.div>
                  </div>
                  

                  
                  {/* Calories Progress Bar */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="space-y-2 mb-4"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-black/90 text-sm font-medium">Calories</span>
                      <span className="text-black text-sm font-bold">{nutritionData.calories} / {nutritionData.calorieGoal}</span>
                    </div>
                    <div className="w-full bg-gray-300 dark:bg-green-800/30 rounded-full h-2">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(100, Math.round((nutritionData.calories / nutritionData.calorieGoal) * 100))}%` }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        className="bg-green-600 h-2 rounded-full"
                      />
                    </div>
                  </motion.div>
                  
                  {/* Macronutrients Grid - 2x2 Layout */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    className="grid grid-cols-2 gap-3 mb-4"
                  >
                    {/* Protein */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-black/90 text-xs">💪 Protein</span>
                        <span className="text-black text-xs font-bold">{nutritionData.protein}g / {nutritionData.proteinGoal}g</span>
                      </div>
                      <div className="w-full bg-gray-300 dark:bg-blue-800/30 rounded-full h-1.5">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, Math.round((nutritionData.protein / nutritionData.proteinGoal) * 100))}%` }}
                          transition={{ duration: 0.8, delay: 0.7 }}
                          className="bg-blue-600 h-1.5 rounded-full"
                        />
                      </div>
                    </div>
                    
                    {/* Carbs */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-black/90 text-xs">🌾 Carbs</span>
                        <span className="text-black text-xs font-bold">{nutritionData.carbs}g / {nutritionData.carbGoal}g</span>
                      </div>
                      <div className="w-full bg-gray-300 dark:bg-amber-800/30 rounded-full h-1.5">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, Math.round((nutritionData.carbs / nutritionData.carbGoal) * 100))}%` }}
                          transition={{ duration: 0.8, delay: 0.8 }}
                          className="bg-amber-600 h-1.5 rounded-full"
                        />
                      </div>
                    </div>
                    
                    {/* Fat */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-black/90 text-xs">🥑 Fat</span>
                        <span className="text-black text-xs font-bold">{nutritionData.fat}g / {nutritionData.fatGoal}g</span>
                      </div>
                      <div className="w-full bg-gray-300 dark:bg-purple-800/30 rounded-full h-1.5">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, Math.round((nutritionData.fat / nutritionData.fatGoal) * 100))}%` }}
                          transition={{ duration: 0.8, delay: 0.9 }}
                          className="bg-purple-600 h-1.5 rounded-full"
                        />
                      </div>
                    </div>
                    
                    {/* Fiber */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-black/90 text-xs">🥬 Fiber</span>
                        <span className="text-black text-xs font-bold">{nutritionData.fiber}g / {nutritionData.fiberGoal}g</span>
                      </div>
                      <div className="w-full bg-gray-300 dark:bg-teal-800/30 rounded-full h-1.5">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, Math.round((nutritionData.fiber / nutritionData.fiberGoal) * 100))}%` }}
                          transition={{ duration: 0.8, delay: 1.0 }}
                          className="bg-teal-600 h-1.5 rounded-full"
                        />
                      </div>
                    </div>
                  </motion.div>
                  
                  {/* Quick Stats Bar */}
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7, duration: 0.5 }}
                    className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100/50 rounded-2xl p-3"
                  >
                    <div className="text-center flex-1">
                      <p className="text-black text-lg font-bold">{nutritionData.mealsToday}</p>
                      <p className="text-black/80 text-xs font-medium">Meals Today</p>
                    </div>
                    <div className="w-px h-8 bg-black/30"></div>
                    <div className="text-center flex-1">
                      <p className="text-black text-lg font-bold">{Math.round((nutritionData.calories / nutritionData.calorieGoal) * 100)}%</p>
                      <p className="text-black/80 text-xs font-medium">Goal Progress</p>
                    </div>
                    <div className="w-px h-8 bg-black/30"></div>
                    <div className="text-center flex-1">
                      <p className="text-black text-lg font-bold">{nutritionData.calorieGoal - nutritionData.calories}</p>
                      <p className="text-black/80 text-xs font-medium">Remaining</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Card 2: Inventory Overview */}
            <div className="w-1/2 flex-shrink-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="relative overflow-hidden bg-white rounded-3xl shadow-lg"
                style={{ minHeight: '200px' }}
              >
                <div className="relative z-10 p-6">
                  {/* Header */}
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="flex items-center justify-between mb-6"
                  >
                    <div className="flex items-center">
                      <h2 className="text-black text-2xl font-heading font-bold">Inventory Overview</h2>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      {/* Bell Button */}
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.35, type: "spring", stiffness: 200 }}
                      >
                        <div className="relative">
                          <Button 
                            ref={pwaBellButtonRef}
                            variant="ghost" 
                            size="icon" 
                            className="relative h-10 w-10 bg-black/20 hover:bg-black/30 text-black border-0 rounded-full backdrop-blur-sm transition-colors duration-200"
                            onClick={() => {
                              setIsPwaAnnouncementDropdownOpen(!isPwaAnnouncementDropdownOpen);
                            }}
                          >
                            <Bell size={18} />
                            {announcementConfigManager.shouldShowRedDot() && (
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                            )}
                          </Button>
                          
                          <AnnouncementDropdown
                            isOpen={isPwaAnnouncementDropdownOpen}
                            onClose={() => setIsPwaAnnouncementDropdownOpen(false)}
                            anchorRef={pwaBellButtonRef}
                          />
                        </div>
                      </motion.div>

                      {/* Settings Button */}
                      <motion.div
                        initial={{ scale: 0, rotate: 180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                      >
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-10 w-10 bg-black/20 hover:bg-black/30 text-black border-0 rounded-full backdrop-blur-sm"
                          onClick={() => navigate('/profile')}
                        >
                          <Settings size={20} />
                        </Button>
                      </motion.div>
                    </div>
                  </motion.div>
                  
                  {/* Motivational Text */}
                  <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="text-black/90 text-base leading-relaxed mb-6"
                  >
                    Let's reduce food waste today!
                  </motion.p>
                  
                  {/* Inventory Stats */}
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                    className="flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100/50 rounded-2xl p-4"
                  >
                    <div className="text-center flex-1">
                      <p className="text-black text-xl font-bold">{inventory.length}</p>
                      <p className="text-black/80 text-sm font-medium">Total Items</p>
                    </div>
                    <div className="w-px h-10 bg-black/30"></div>
                    <div className="text-center flex-1">
                      <p className="text-black text-xl font-bold">{expiringItems.length}</p>
                      <p className="text-black/80 text-sm font-medium">Expiring</p>
                    </div>
                    <div className="w-px h-10 bg-black/30"></div>
                    <div className="text-center flex-1">
                      <p className="text-black text-xl font-bold">{inventory.filter(item => daysUntilExpiry(item.expiryDate) > 7).length}</p>
                      <p className="text-black/80 text-sm font-medium">Fresh</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Swipe indicators */}
          <div className="flex justify-center mt-4 space-x-2">
            <div 
              className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                currentCard === 0 ? 'bg-pink-500' : 'bg-gray-300'
              }`}
            />
            <div 
              className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                currentCard === 1 ? 'bg-pink-500' : 'bg-gray-300'
              }`}
            />
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className={`space-y-4 ${isMobile ? 'px-0' : ''}`}>
        
        {/* Desktop Layout */}
        {!isMobile && (
          <div className="grid gap-3 grid-cols-3 max-w-md">
            <QuickActionCard
              icon={null}
              title="Scan Items"
              subtitle=""
              color="bg-blue-100 dark:bg-slate-800/60 shadow-sm"
              iconColor=""
              isMobile={isMobile}
              backgroundImage="/scanbutton.png"
              hoverImage="/scan_anim.webp"
              onClick={() => {
                triggerFileInput();
              }}
            />
            <QuickActionCard
              icon={null}
              title="Meal Analyzer"
              subtitle=""
              color="bg-teal-100 dark:bg-slate-800/60 shadow-sm"
              iconColor=""
              isMobile={isMobile}
              backgroundImage="/photobutton.png"
              hoverImage="/photobutton.webp"
              onClick={() => {
                triggerMealScanInput();
              }}
            />
            <QuickActionCard
              icon={null}
              title="AI Recipes"
              subtitle=""
              color="bg-purple-100 dark:bg-slate-800/60 shadow-sm"
              iconColor=""
              isMobile={isMobile}
              backgroundImage="/AIrecipebutton.png"
              hoverImage="/cooking_anim.webp"
              onClick={() => showIngredientSourceModal()}
            />
          </div>
        )}

        {/* Mobile Layout */}
        {isMobile && (
          <div className="space-y-3">
            {/* First Row - Main 3 Buttons */}
            <div className="grid grid-cols-3 gap-2">
              <QuickActionCard
                icon={null}
                title="Scan Items"
                subtitle=""
                color="bg-blue-100 dark:bg-slate-800/60 border-blue-200 dark:border-slate-700/50"
                iconColor=""
                isMobile={isMobile}
                backgroundImage="/scan_anim.webp"
                onClick={() => {
                  triggerFileInput();
                }}
              />
              <QuickActionCard
                icon={null}
                title="Meal Analyzer"
                subtitle=""
                color="bg-teal-100 dark:bg-slate-800/60 border-teal-200 dark:border-slate-700/50"
                iconColor=""
                isMobile={isMobile}
                backgroundImage="/photobutton.webp"
                onClick={() => {
                  triggerMealScanInput();
                }}
              />
              <QuickActionCard
                icon={null}
                title="AI Recipes"
                subtitle=""
                color="bg-purple-100 dark:bg-slate-800/60 border-purple-200 dark:border-slate-700/50"
                iconColor=""
                isMobile={isMobile}
                backgroundImage="/cooking_anim.webp"
                onClick={() => showIngredientSourceModal()}
              />
            </div>

            {/* Second Row - Navigation Buttons */}
            <div className="grid grid-cols-3 gap-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.1, ease: "easeOut" }}
                className="bg-amber-50 dark:bg-slate-800/60 border border-amber-100 dark:border-amber-800/50 rounded-xl p-3 cursor-pointer transition-all duration-75 focus:outline-none"
                onClick={() => navigate('/tracker')}
              >
                <div className="flex items-center justify-center gap-2">
                  <Activity size={22} className="text-amber-600 dark:text-amber-400" />
                  <span className="font-medium text-foreground text-sm">Meal Logs</span>
                </div>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.1, ease: "easeOut" }}
                className="bg-purple-50 dark:bg-slate-800/60 border border-purple-100 dark:border-purple-800/50 rounded-xl p-3 cursor-pointer transition-all duration-75 focus:outline-none"
                onClick={() => navigate('/planner')}
              >
                <div className="flex items-center justify-center gap-2">
                  <CalendarDays size={22} className="text-purple-600 dark:text-purple-400" />
                  <span className="font-medium text-foreground text-sm">Meal Planner</span>
                </div>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.1, ease: "easeOut" }}
                className="bg-emerald-50 dark:bg-slate-800/60 border border-emerald-100 dark:border-emerald-800/50 rounded-xl p-3 cursor-pointer transition-all duration-75 focus:outline-none"
                onClick={() => navigate('/shopping')}
              >
                <div className="flex items-center justify-center gap-2">
                  <ShoppingCart size={22} className="text-emerald-600 dark:text-emerald-400" />
                  <span className="font-medium text-foreground text-sm">Shopping List</span>
                </div>
              </motion.button>
            </div>
          </div>
        )}

        {/* Hidden file inputs */}
        <div style={{ display: 'none' }}>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            ref={fileInputRef}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                console.log('🖥️ [Desktop] File selected:', {
                  name: file.name,
                  size: Math.round(file.size / 1024) + 'KB',
                  type: file.type
                });
                // Use global event system to avoid duplicate calls
                const inventoryScanEvent = new CustomEvent('triggerInventoryScan', { detail: { file } });
                window.dispatchEvent(inventoryScanEvent);
              }
            }}
            disabled={isMobile}
          />
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            ref={mealScanInputRef}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                console.log('🖥️ [Desktop] Meal file selected:', {
                  name: file.name,
                  size: Math.round(file.size / 1024) + 'KB',
                  type: file.type
                });
                // Use global event system to avoid duplicate calls
                const mealScanEvent = new CustomEvent('triggerMealScan', { detail: { file } });
                window.dispatchEvent(mealScanEvent);
              }
            }}
            disabled={isMobile}
          />

          {/* Mobile camera inputs for quick actions */}
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            capture="environment"
            ref={mobileInventoryCameraRef}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                console.log('📱 [Mobile] Inventory camera file selected:', {
                  name: file.name,
                  size: Math.round(file.size / 1024) + 'KB',
                  type: file.type
                });
                // Use global event system to avoid duplicate calls
                const inventoryScanEvent = new CustomEvent('triggerInventoryScan', { detail: { file } });
                window.dispatchEvent(inventoryScanEvent);
              }
            }}
            disabled={!isMobile}
          />
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            capture="environment"
            ref={mobileMealCameraRef}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                console.log('📱 [Mobile] Meal camera file selected:', {
                  name: file.name,
                  size: Math.round(file.size / 1024) + 'KB',
                  type: file.type
                });
                // Use global event system to avoid duplicate calls
                const mealScanEvent = new CustomEvent('triggerMealScan', { detail: { file } });
                window.dispatchEvent(mealScanEvent);
              }
            }}
            disabled={!isMobile}
          />
        </div>

        {isScanning && (
          <div className="text-center text-blue-600 font-medium py-2">Analyzing image...</div>
        )}
      </div>

      {/* Expires Soon Section - Mobile Only */}
      {isMobile && (
        <section className="space-y-4">
          <div className="flex items-center justify-between px-4">
            <h2 className="text-lg font-heading font-semibold text-foreground">Expires Soon</h2>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-primary hover:text-primary-hover"
              onClick={() => navigate('/inventory')}
            >
              View All
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
          
          {expiringItems.length > 0 ? (
            <div className="w-full overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="flex gap-3 pb-2 pl-4 pr-4" style={{ width: 'max-content' }}>
                {expiringItems.slice(0, 10).map((item) => {
                  const days = daysUntilExpiry(item.expiryDate);
                  const getExpiryBgColor = (days: number, location: string): string => {
                    if (location === 'freezer') return 'bg-cyan-50 dark:bg-cyan-900/10';
                    if (days < 0) return 'bg-red-100 dark:bg-red-900/20';
                    if (days === 0) return 'bg-red-100 dark:bg-red-900/20';
                    if (days === 1) return 'bg-orange-100 dark:bg-orange-900/15';
                    if (days === 2) return 'bg-orange-50 dark:bg-orange-900/7';
                    if (days === 3) return 'bg-yellow-50 dark:bg-yellow-900/10';
                    return 'bg-green-50 dark:bg-green-900/10';
                  };
                  
                  const getExpiryTextColor = (days: number, location: string): string => {
                    if (location === 'freezer') return 'bg-cyan-100 text-cyan-700 dark:text-cyan-300 border border-cyan-200';
                    if (days < 0) return 'bg-destructive/20 text-destructive border border-destructive/30';
                    if (days < 3) return 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/20';
                    if (days < 7) return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20';
                    return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20';
                  };
                  
                  const getExpiryText = (days: number, location: string): string => {
                    if (location === 'freezer') return 'Frozen';
                    if (days < 0) return 'Expired';
                    if (days === 0) return 'Today';
                    if (days === 1) return '1 day';
                    return `${days} days`;
                  };

                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`flex-shrink-0 w-32 ${getExpiryBgColor(days, item.location)} rounded-xl p-3 border border-border/50 shadow-sm hover:shadow-md transition-all duration-75`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.08, ease: "easeOut" }}
                      onClick={() => navigate('/inventory')}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">{item.emoji}</div>
                        <h3 className="font-semibold text-sm text-foreground mb-2 truncate">{item.name}</h3>
                        <span className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${getExpiryTextColor(days, item.location)}`}>
                          {getExpiryText(days, item.location)}
                        </span>
                      </div>
                    </motion.div>
                  );
                                 })}
               </div>
             </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground px-4">
              <CalendarDays size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No items expiring soon</p>
            </div>
          )}
        </section>
      )}

      {/* Recent Recipes */}
      <section className="space-y-4">
        <div className={`flex items-center justify-between ${isMobile ? 'px-4' : ''}`}>
          <div className="flex items-center gap-2">
            <span className="text-xl">🍽️</span>
            <h2 className="text-lg font-heading font-semibold text-foreground">Recent Recipes</h2>
            <span className="text-sm text-muted-foreground">
              ({recipes.length})
            </span>
          </div>
          {isMobile && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-primary hover:text-primary-hover"
              onClick={() => navigate('/recipes')}
            >
              View All
              <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
        </div>
        
        {recipes.length > 0 ? (
          isMobile ? (
                        // Mobile: Horizontal Scrolling with Lazy Loading
            <div className="relative">
              <div 
                ref={recipeScrollRef}
                className="flex gap-4 overflow-x-auto scrollbar-hide py-4 px-4"
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch'
                }}
                onScroll={handleRecipeScroll}
              >
                {recipes
                   .slice(0, visibleRecipeCount)
                  .map((recipe) => {
                    const handleRecipeClick = async () => {
                      console.log('🍳 [Dashboard Recipe Card] Opening saved recipe:', recipe.title);
                      await useNoshStore.getState().showSavedRecipe(recipe);
                    };

                    return (
                      <div 
                        key={recipe.id}
                        className="flex-shrink-0 min-w-72 max-w-80 px-2"
                        style={{ scrollSnapAlign: 'start' }}
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          transition={{ duration: 0.08, ease: "easeOut" }}
                          className="relative cursor-pointer transition-all duration-75"
                          onClick={handleRecipeClick}
                        >
                          {/* Food image - Smaller size for mobile */}
                          <div className="relative z-10 flex justify-center w-full -mb-32" style={{ transform: 'translateY(-16px)' }}>
                            <div className="w-64 h-64">
                              <CachedImage
                                src={getOptimizedImageUrl(recipe.imageUrl, 'horizontal-card', { 
                                  fallbackUrl: '/img/small 1.png',
                                  quality: 85 
                                })}
                                alt={recipe.title}
                                className="w-full h-full rounded-2xl object-cover"
                                loading="lazy"
                                fallbackSrc="/img/small 1.png"
                                preserveAspectRatio={true}
                                aspectRatio="1/1"
                              />
                            </div>
                          </div>
                          
                          {/* Card positioned behind the image */}
                          <div className="bg-white dark:bg-[#1d2839] rounded-2xl border border-gray-100 dark:border-slate-700/50 shadow-[0_4px_20px_rgb(0,0,0,0.08)] -mt-16 pt-24 pb-4 px-4">
                            <div className="text-center">
                              <div className="h-12 flex items-center justify-center mb-2">
                                <h3 className="font-heading font-semibold text-foreground line-clamp-2 text-base text-center leading-tight">{recipe.title}</h3>
                              </div>
                              
                              <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm mb-2">
                                <span className="flex items-center">
                                  <span className="mr-1">⏱️</span>
                                  {recipe.time} min
                                </span>
                                <span className="flex items-center">
                                  <span className="mr-1">🔥</span>
                                  {recipe.calories} cal
                                </span>
                              </div>
                              
                              {/* Nutrition Progress Bars - Copied exactly from Recipes page mobile */}
                              {(() => {
                                const nutrition = calculateNutritionPercentages(recipe);
                                return (
                                  <div className="mb-4">
                                    <div className="space-y-2 text-sm">
                                      {/* Protein */}
                                      <div className="flex items-center">
                                        <div className="flex items-center w-16">
                                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                          <span className="text-muted-foreground text-sm">Protein</span>
                                        </div>
                                        <div className="flex items-center flex-1 ml-3">
                                          <div className="bg-muted rounded-full overflow-hidden w-32 h-2">
                                            <motion.div
                                              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                                              initial={{ width: "0%" }}
                                              animate={{ width: `${Math.round(nutrition.protein.percentage)}%` }}
                                              transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
                                            />
                                          </div>
                                          <span className="ml-2 font-medium text-foreground text-sm">
                                            {nutrition.protein.value}g
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {/* Carbs */}
                                      <div className="flex items-center">
                                        <div className="flex items-center w-16">
                                          <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
                                          <span className="text-muted-foreground text-sm">Carbs</span>
                                        </div>
                                        <div className="flex items-center flex-1 ml-3">
                                          <div className="bg-muted rounded-full overflow-hidden w-32 h-2">
                                            <motion.div
                                              className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full"
                                              initial={{ width: "0%" }}
                                              animate={{ width: `${Math.round(nutrition.carbs.percentage)}%` }}
                                              transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
                                            />
                                          </div>
                                          <span className="ml-2 font-medium text-foreground text-sm">
                                            {nutrition.carbs.value}g
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {/* Fat */}
                                      <div className="flex items-center">
                                        <div className="flex items-center w-16">
                                          <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                                          <span className="text-muted-foreground text-sm">Fat</span>
                                        </div>
                                        <div className="flex items-center flex-1 ml-3">
                                          <div className="bg-muted rounded-full overflow-hidden w-32 h-2">
                                            <motion.div
                                              className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full"
                                              initial={{ width: "0%" }}
                                              animate={{ width: `${Math.round(nutrition.fat.percentage)}%` }}
                                              transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
                                            />
                                          </div>
                                          <span className="ml-2 font-medium text-foreground text-sm">
                                            {nutrition.fat.value}g
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                
                {/* Loading indicator when there are more recipes */}
                {visibleRecipeCount < recipes.length && (
                  <div className="flex-shrink-0 w-72 px-2 flex items-center justify-center">
                    <div className="bg-white dark:bg-[#1d2839] rounded-2xl border border-gray-100 dark:border-slate-700/50 shadow-sm p-8 text-center">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <p className="text-sm text-muted-foreground">Loading more recipes...</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {visibleRecipeCount} of {recipes.length}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
                    ) : (
            // Desktop: Grid layout with Load More (fixed 3 columns)
            <div className="space-y-6">
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8 2xl:gap-10"
              >
                {recipes
                   .slice(0, desktopVisibleCount)
                .map((recipe) => {
                  const handleRecipeClick = async () => {
                    console.log('🍳 [Dashboard Recipe Card] Opening saved recipe:', recipe.title);
                    await useNoshStore.getState().showSavedRecipe(recipe);
                  };

                  return (
                    <motion.div
                      key={recipe.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.08, ease: "easeOut" }}
                      className="relative cursor-pointer transition-all duration-75 min-w-72 max-w-80 flex-1"
                      onClick={handleRecipeClick}
                    >
                      {/* Food image positioned to "pop out" */}
                      <div className="relative z-10 mx-auto w-75 h-62 -mb-36">
                        <CachedImage
                          src={getOptimizedImageUrl(recipe.imageUrl, 'recipe-card', { 
                            fallbackUrl: '/img/small 1.png',
                            quality: 85 
                          })}
                          alt={recipe.title}
                          className="w-full h-full rounded-2xl object-cover"
                          loading="lazy"
                          fallbackSrc="/img/small 1.png"
                          preserveAspectRatio={true}
                          aspectRatio="1/1"
                        />
                      </div>
                      
                      {/* Card positioned behind the image */}
                      <div className="bg-white dark:bg-[#1d2839] rounded-2xl border border-gray-100 dark:border-slate-700/50 shadow-[0_4px_20px_rgb(0,0,0,0.08)] -mt-16 pt-32 pb-3 px-4">
                        <div className="text-center">
                          <div className="h-14 flex items-center justify-center mb-2">
                            <h3 className="font-heading font-semibold text-foreground line-clamp-2 text-lg text-center leading-tight">{recipe.title}</h3>
                          </div>
                          
                          {/* Hero Ingredients - Desktop only */}
                          {(() => {
                            const heroIngredients = getHeroIngredients(recipe);
                            const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
                              'poultry': { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-800/30' },
                              'meat': { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', border: 'border-red-800/30' },
                              'fish': { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-800/30' },
                              'grains': { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-800/30' },
                              'vegetables': { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300', border: 'border-green-800/30' },
                              'dairy': { bg: 'bg-indigo-100 dark:bg-indigo-900/40', text: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-800/30' },
                              'default': { bg: 'bg-gray-100 dark:bg-gray-900/40', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-800/30' }
                            };
                            
                            return (
                              <div className="mb-2">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                  {heroIngredients.map((ingredient: { name: string; emoji: string; category: string }, index: number) => {
                                    const colors = categoryColors[ingredient.category] || categoryColors.default;
                                    return (
                                      <div key={index} className={`flex items-center ${colors.bg} rounded-full px-2 py-0.5 border dark:${colors.border}`}>
                                        <span className="text-xs mr-1">{ingredient.emoji}</span>
                                        <span className={`text-xs font-medium ${colors.text}`}>{ingredient.name}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                          
                          <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm mb-2">
                            <span className="flex items-center">
                              <span className="mr-1">⏱️</span>
                              {recipe.time} min
                            </span>
                            <span className="flex items-center">
                              <span className="mr-1">🔥</span>
                              {recipe.calories} cal
                            </span>
                          </div>
                          
                          {/* Nutrition Progress Bars */}
                          {(() => {
                            const nutrition = calculateNutritionPercentages(recipe);
                            return (
                              <div className="mb-3">
                                <div className="space-y-1 text-sm">
                                  {/* Protein */}
                                  <div className="flex items-center">
                                    <div className="flex items-center w-16">
                                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                      <span className="text-muted-foreground text-sm">Protein</span>
                                    </div>
                                    <div className="flex items-center flex-1 ml-3">
                                      <div className="bg-muted rounded-full overflow-hidden w-32 h-2">
                                        <motion.div
                                          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                                          initial={{ width: "0%" }}
                                          animate={{ width: `${Math.round(nutrition.protein.percentage)}%` }}
                                          transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
                                        />
                                      </div>
                                      <span className="ml-2 font-medium text-foreground text-sm">
                                        {nutrition.protein.value}g
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Carbs */}
                                  <div className="flex items-center">
                                    <div className="flex items-center w-16">
                                      <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
                                      <span className="text-muted-foreground text-sm">Carbs</span>
                                    </div>
                                    <div className="flex items-center flex-1 ml-3">
                                      <div className="bg-muted rounded-full overflow-hidden w-32 h-2">
                                        <motion.div
                                          className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full"
                                          initial={{ width: "0%" }}
                                          animate={{ width: `${Math.round(nutrition.carbs.percentage)}%` }}
                                          transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
                                        />
                                      </div>
                                      <span className="ml-2 font-medium text-foreground text-sm">
                                        {nutrition.carbs.value}g
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Fat */}
                                  <div className="flex items-center">
                                    <div className="flex items-center w-16">
                                      <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                                      <span className="text-muted-foreground text-sm">Fat</span>
                                    </div>
                                    <div className="flex items-center flex-1 ml-3">
                                      <div className="bg-muted rounded-full overflow-hidden w-32 h-2">
                                        <motion.div
                                          className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full"
                                          initial={{ width: "0%" }}
                                          animate={{ width: `${Math.round(nutrition.fat.percentage)}%` }}
                                          transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
                                        />
                                      </div>
                                      <span className="ml-2 font-medium text-foreground text-sm">
                                        {nutrition.fat.value}g
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              
              {/* Load More Button */}
              {desktopVisibleCount < recipes.length && (
                <div className="flex justify-center">
                  <Button
                    onClick={handleLoadMoreRecipes}
                    variant="outline"
                    className="px-6 py-2 text-sm font-medium rounded-xl border-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200"
                  >
                    Load More Recipes
                  </Button>
                </div>
              )}
            </div>
          )
        ) : (
          // Empty state
          <div className={`text-center py-8 text-muted-foreground ${isMobile ? 'px-4' : ''}`}>
            <ChefHat size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No recipes created yet</p>
          </div>
        )}
      </section>

      {/* Recent Meal Logs */}
      <section className="space-y-4">
        <div className={`flex items-center justify-between ${isMobile ? 'px-4' : ''}`}>
          <div className="flex items-center gap-2">
            <ClipboardList size={20} className="text-primary" />
            <h2 className="text-lg font-heading font-semibold text-foreground">Recent Meal Logs</h2>
            <span className="text-sm text-muted-foreground">({meals.length})</span>
          </div>
          {isMobile && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-primary hover:text-primary-hover"
              onClick={() => navigate('/tracker')}
            >
              View All
              <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
        </div>
        
        {meals && meals.length > 0 ? (
          isMobile ? (
            // Mobile: Horizontal Scrolling with Lazy Loading
            <div className="relative">
              <div 
                ref={mealScrollRef}
                className="flex gap-4 overflow-x-auto scrollbar-hide py-4 px-4"
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch'
                }}
                onScroll={handleMealScroll}
              >
                {meals
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .slice(0, visibleMealCount)
                  .map((meal) => {
                    return (
                      <div 
                        key={meal.id}
                        className="flex-shrink-0 w-72 px-2"
                        style={{ scrollSnapAlign: 'start' }}
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          transition={{ duration: 0.08, ease: "easeOut" }}
                          className="bg-card rounded-xl shadow-soft overflow-hidden cursor-pointer border border-border hover:shadow-lg transition-all duration-75"
                          onClick={() => handleMealClick(meal.id)}
                        >
                        {/* Logging Date Banner */}
                        <div className="relative bg-gradient-to-r from-rose-50 via-pink-50 to-purple-50 dark:from-slate-800/60 dark:via-slate-800/40 dark:to-slate-700/60 border-b border-rose-100/50 dark:border-slate-600/30">
                          <div className="absolute inset-0 bg-gradient-to-r from-white/60 to-transparent dark:from-slate-700/30 dark:to-transparent"></div>
                          <div className="relative px-4 py-3 flex items-center justify-center gap-2">
                            <div className="w-1.5 h-1.5 bg-gradient-to-r from-rose-400 to-pink-400 rounded-full opacity-70"></div>
                            <span className="text-slate-600 dark:text-slate-300 text-sm font-medium tracking-wide">
                              {formatLoggingDate(meal.createdAt)}
                            </span>
                            <div className="w-1.5 h-1.5 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full opacity-70"></div>
                          </div>
                        </div>
                        
                        {/* Meal Image - Same as MealTracker */}
                        <div className="relative">
                          <CachedMealImage
                            imagePathOrUrl={meal.imageUrl}
                            alt={meal.mealName}
                            className="w-full h-48 object-cover"
                          />
                          {/* Only show confidence score for photo_scan meals, not recipe meals */}
                          {meal.source === 'photo_scan' && (
                            <div className="absolute top-3 right-3">
                              <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                {meal.confidence}% confident
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Meal Info - Same as MealTracker */}
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-heading font-semibold text-foreground text-sm leading-tight">
                              {meal.mealName}
                            </h3>
                          </div>
                          
                          <div className="flex items-center text-xs text-muted-foreground mb-3 gap-2">
                            <Calendar size={12} />
                            <span>{formatDate(meal.createdAt)}</span>
                          </div>
                          
                          {/* Nutrition Summary - Same as MealTracker */}
                          <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                            {/* Calories - Green */}
                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg p-2">
                              <div className="text-sm font-bold text-green-700 dark:text-green-300">{meal.calories}</div>
                              <div className="text-xs text-green-600 dark:text-green-400">cal</div>
                            </div>
                            {/* Protein - Blue */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-2">
                              <div className="text-sm font-bold text-blue-700 dark:text-blue-300">{meal.protein}g</div>
                              <div className="text-xs text-blue-600 dark:text-blue-400">protein</div>
                            </div>
                            {/* Carbs - Yellow/Amber */}
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg p-2">
                              <div className="text-sm font-bold text-amber-700 dark:text-amber-300">{meal.carbs}g</div>
                              <div className="text-xs text-amber-600 dark:text-amber-400">carbs</div>
                            </div>
                          </div>
                          
                          {/* Ingredients Preview - Same as MealTracker */}
                          <div className="text-xs text-muted-foreground">
                            <span className="font-medium">Ingredients: </span>
                            <span>
                              {meal.ingredients.slice(0, 3).join(', ')}
                              {meal.ingredients.length > 3 && ` +${meal.ingredients.length - 3} more`}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                    );
                  })}
                
                {/* Loading indicator when there are more meals */}
                {visibleMealCount < meals.length && (
                  <div className="flex-shrink-0 w-72 px-2 flex items-center justify-center">
                    <div className="bg-white dark:bg-[#1d2839] rounded-2xl border border-gray-100 dark:border-slate-700/50 shadow-sm p-8 text-center">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <p className="text-sm text-muted-foreground">Loading more meals...</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {visibleMealCount} of {meals.length}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Desktop: Grid layout with Load More (fixed 3 columns)
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                {meals
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .slice(0, desktopVisibleMealCount)
                  .map((meal) => {
                    return (
                      <motion.div
                        key={meal.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.08, ease: "easeOut" }}
                        className="bg-card rounded-xl shadow-soft overflow-hidden cursor-pointer border border-border hover:shadow-lg transition-all duration-75"
                        onClick={() => handleMealClick(meal.id)}
                      >
                    {/* Logging Date Banner */}
                    <div className="relative bg-gradient-to-r from-rose-50 via-pink-50 to-purple-50 dark:from-slate-800/60 dark:via-slate-800/40 dark:to-slate-700/60 border-b border-rose-100/50 dark:border-slate-600/30">
                      <div className="absolute inset-0 bg-gradient-to-r from-white/60 to-transparent dark:from-slate-700/30 dark:to-transparent"></div>
                      <div className="relative px-4 py-3 flex items-center justify-center gap-2">
                        <div className="w-1.5 h-1.5 bg-gradient-to-r from-rose-400 to-pink-400 rounded-full opacity-70"></div>
                        <span className="text-slate-600 dark:text-slate-300 text-sm font-medium tracking-wide">
                          {formatLoggingDate(meal.createdAt)}
                        </span>
                        <div className="w-1.5 h-1.5 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full opacity-70"></div>
                      </div>
                    </div>
                    
                    {/* Meal Image */}
                    <div className="relative">
                      <CachedMealImage
                        imagePathOrUrl={meal.imageUrl}
                        alt={meal.mealName}
                        className="w-full h-48 object-cover"
                      />
                      {/* Only show confidence score for photo_scan meals, not recipe meals */}
                      {meal.source === 'photo_scan' && (
                        <div className="absolute top-3 right-3">
                          <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                            {meal.confidence}% confident
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Meal Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-heading font-semibold text-foreground text-sm leading-tight">
                          {meal.mealName}
                        </h3>
                      </div>
                      
                      <div className="flex items-center text-xs text-muted-foreground mb-3 gap-2">
                        <Calendar size={12} />
                        <span>{formatDate(meal.createdAt)}</span>
                      </div>
                      
                      {/* Nutrition Summary */}
                      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                        {/* Calories - Green */}
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg p-2">
                          <div className="text-sm font-bold text-green-700 dark:text-green-300">{meal.calories}</div>
                          <div className="text-xs text-green-600 dark:text-green-400">cal</div>
                        </div>
                        {/* Protein - Blue */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-2">
                          <div className="text-sm font-bold text-blue-700 dark:text-blue-300">{meal.protein}g</div>
                          <div className="text-xs text-blue-600 dark:text-blue-400">protein</div>
                        </div>
                        {/* Carbs - Yellow/Amber */}
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg p-2">
                          <div className="text-sm font-bold text-amber-700 dark:text-amber-300">{meal.carbs}g</div>
                          <div className="text-xs text-amber-600 dark:text-amber-400">carbs</div>
                        </div>
                      </div>
                      
                      {/* Ingredients Preview */}
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">Ingredients: </span>
                        <span>
                          {meal.ingredients.slice(0, 3).join(', ')}
                          {meal.ingredients.length > 3 && ` +${meal.ingredients.length - 3} more`}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                    );
                  })}
              </div>
              
              {/* Load More Button */}
              {desktopVisibleMealCount < meals.length && (
                <div className="flex justify-center">
                  <Button
                    onClick={handleLoadMoreMeals}
                    variant="outline"
                    className="px-6 py-2 text-sm font-medium rounded-xl border-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200"
                  >
                    Load More Meals
                  </Button>
                </div>
              )}
            </div>
          )
        ) : (
          // Empty state
          <div className={`text-center py-8 text-muted-foreground ${isMobile ? 'px-4' : ''}`}>
            <ClipboardList size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No meals scanned yet</p>
            <p className="text-xs mt-1">Use the meal scanner to track your nutrition</p>
          </div>
        )}
      </section>

      {/* Ingredient Source Selection Modal */}
      <IngredientSourceModal
        isOpen={ingredientSource.showModal}
        onClose={hideIngredientSourceModal}
        onSourceSelected={handleIngredientSourceSelected}
        inventoryCount={inventory.length}
        isMobile={isMobile}
      />

      {/* Nutrition Analysis Modal */}
      <NutritionAnalysisModal
        isOpen={showNutritionModal}
        onClose={handleNutritionModalClose}
        mode="view"
        mealId={selectedMealId}
      />

      {/* Meal Generation Modal */}
      <MealGenerationModal
        isOpen={showRecipeModal}
        onClose={() => setShowRecipeModal(false)}
        onBack={handleMealGenerationBack}
        onGenerate={handleRecipeGenerate}
        inventoryCount={inventory.length}
        shoppingItemsCount={0} // TODO: Get actual shopping items count from store
        detectedIngredients={ingredientSource.detectedIngredients}
      />

      {/* Usage Limit Modal now handled globally in App.tsx */}
    </div>
  );
};

const QuickActionCard = ({ 
  icon, 
  title, 
  subtitle, 
  color, 
  iconColor,
  isMobile,
  backgroundImage,
  hoverImage,
  onClick
}: { 
  icon: React.ReactNode, 
  title: string, 
  subtitle: string, 
  color: string, 
  iconColor: string,
  isMobile: boolean,
  backgroundImage?: string,
  hoverImage?: string,
  onClick?: () => void
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      whileHover={{ scale: 1.08, y: -4 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.08, ease: "easeOut" }}
      className={`rounded-xl cursor-pointer border transition-all duration-75 relative overflow-hidden group focus:outline-none ${
        backgroundImage ? 'p-0' : (isMobile ? 'p-3' : 'p-4')
      } ${backgroundImage ? '' : color}`}
      tabIndex={0}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...(backgroundImage ? { 
          aspectRatio: '320/240',
          minHeight: isMobile ? '90px' : '70px' 
        } : {}),
        // 3D Button Effects
        boxShadow: isHovered 
          ? '0 20px 40px rgba(0, 0, 0, 0.15), inset 0 -3px 0 rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
          : '0 8px 25px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
        transform: isHovered 
          ? 'translateY(-2px) rotateX(-5deg)' 
          : 'translateY(0px) rotateX(0deg)',
        transformStyle: 'preserve-3d',
        perspective: '1000px'
      }}
    >
      {/* 3D Inner Shadow for Depth */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-white/30 via-transparent to-black/10 pointer-events-none" />
      
      {/* Hover gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {backgroundImage ? (
        // New layout with background image filling most of button, text below
        <div className="relative z-10 w-full h-full flex flex-col">
          {/* Background image covering most of button with subtle pastel background */}
          <div 
            className={`flex-1 bg-cover bg-center bg-no-repeat rounded-t-xl transition-all duration-300 ${
              color.includes('blue') ? 'bg-blue-50/50' :
              color.includes('teal') ? 'bg-teal-50/50' :
              color.includes('purple') ? 'bg-purple-50/50' :
              ''
            }`}
            style={{ 
              backgroundImage: `url(${isHovered && hoverImage ? hoverImage : backgroundImage})`
            }}
          />
          
          {/* Text area below image */}
          <div className={`${isMobile ? 'p-1.5' : 'p-2'} text-center ${color} rounded-b-xl`}>
            <h3 className={`font-semibold text-foreground ${isMobile ? 'text-xs' : 'text-sm'} leading-tight`}>
              {title}
            </h3>
            {!isMobile && subtitle && (
              <p className="text-xs text-muted-foreground font-medium mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      ) : (
        // Original layout for other buttons
        <div className={`relative z-10 ${color}`}>
          <div className={`${iconColor} ${isMobile ? 'mb-2' : 'mb-3'} flex justify-center transform group-hover:scale-110 transition-transform duration-300`}>
            {icon}
          </div>
          <div className="text-center">
            <h3 className={`font-semibold text-foreground ${isMobile ? 'text-sm' : 'text-sm'} ${isMobile ? 'mb-0' : 'mb-1'} leading-tight`}>
              {title}
            </h3>
            {!isMobile && subtitle && (
              <p className="text-xs text-muted-foreground font-medium">{subtitle}</p>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Dashboard;
